import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { ImagePlus, Loader2, Trash2 } from "lucide-react";

interface ImageUploadProps {
  /** Base route, e.g. "/api/Product/Image" or "/api/Product/VariantImage" */
  route: string;
  id: string;
  hasImage: boolean;
  /** Changes when the image does, so the browser refetches it */
  version?: string;
  canEdit: boolean;
  onChanged: () => void;
  size?: "sm" | "lg";
}

const MAX_BYTES = 5 * 1024 * 1024;

/** A product or variant photo with upload and remove; the image is fetched with the login cookie */
export function ImageUpload({ route, id, hasImage, version, canEdit, onChanged, size = "lg" }: ImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const { success, error } = useToast();
  const box = size === "lg" ? "size-24" : "size-12";

  const upload = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      error("Image too large", "Choose an image under 5 MB");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.upload(`${route}/${id}`, fd);
      success("Photo saved", "The image was uploaded");
      onChanged();
    } catch (err: any) {
      error("Upload failed", err.message || "Could not upload the image");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`${route}/${id}`);
      onChanged();
    } catch (err: any) {
      error("Remove failed", err.message || "Could not remove the image");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      <div className={`${box} rounded-lg border border-border bg-muted/40 overflow-hidden flex items-center justify-center shrink-0`}>
        {hasImage ? (
          <img src={`${route}/${id}?v=${encodeURIComponent(version || "")}`} alt="" className="size-full object-cover" />
        ) : (
          <ImagePlus className="size-5 text-muted-foreground" />
        )}
      </div>
      {canEdit && (
        <div className="flex flex-col gap-1.5">
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
          <Button type="button" variant="outline" size="sm" className="h-8 text-xs" disabled={busy} onClick={() => inputRef.current?.click()}>
            {busy ? <Loader2 className="size-3.5 animate-spin mr-1.5" /> : null}
            {hasImage ? "Replace" : "Upload"}
          </Button>
          {hasImage && (
            <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-red-600" disabled={busy} onClick={remove}>
              <Trash2 className="size-3.5 mr-1" />
              Remove
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
